import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { of } from 'rxjs';
import { filter, map, pluck, skip, switchMap, tap } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { createEffectFactory } from '@recruitee/ngrx';

import { BillingPlansHttpClient } from '../../billing-plans-http-client';
import { BillingPlansActions } from './billing-plans.store';

@Injectable({ providedIn: 'root' })
export class BillingPlansEffects implements OnInitEffects {
  public effect = createEffectFactory(BillingPlansActions, this.actions$);

  public fetchBillingPlans$ = this.effect(BillingPlansActions.fetch, () =>
    this.billingPlansHttpClient.fetchBillingPlans().pipe(filterBoolean),
  );

  public fetchActiveBillingPlan$ = this.effect(BillingPlansActions.fetchActivePlan, () =>
    this.billingPlansHttpClient
      .fetchActiveBillingPlan()
      .pipe(
        map(billingPlan => BillingPlansActions.fetchActivePlanSuccess({ payload: billingPlan })),
      ),
  );

  // Redirect to billing after adding plan
  public activePlanChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingPlansActions.fetchActivePlanSuccess),
        skip(1), // Skip initial fetching
        pluck('payload', 'plan'),
        filterBoolean,
        switchMap(() => of(this.router.url)),
        filter(url => url.includes('pricing/trial')),
        tap(() =>
          this.router.navigate(['/settings/pricing/billing'], {
            replaceUrl: true,
            state: { showPlanChangeMessage: true },
          }),
        ),
      ),
    { dispatch: false },
  );

  // Redirect to trial after deleting plan
  public activePlanDeleted$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingPlansActions.fetchActivePlanSuccess),
        skip(1), // Skip initial fetching
        pluck('payload', 'plan'),
        filter(plan => plan === null),
        switchMap(() => of(this.router.url)),
        filter(url => url.includes('settings/pricing/billing')),
        tap(() => this.router.navigate(['/settings/pricing/trial/active'])),
      ),
    { dispatch: false },
  );

  constructor(
    private billingPlansHttpClient: BillingPlansHttpClient,
    private actions$: Actions,
    private router: Router,
  ) {}

  public ngrxOnInitEffects(): Action {
    return BillingPlansActions.fetch();
  }
}
